import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import './i18n/config';

// Components (loaded immediately - needed for all pages)
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import HomePage from './pages/HomePage'; // Eager load for LCP

// Lazy load pages for code splitting
// HomePage removed from lazy load
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const LegacyWealthPage = lazy(() => import('./pages/LegacyWealthPage'));
const BusinessConsultingPage = lazy(() => import('./pages/BusinessConsultingPage'));
const OCPDExecutiveCoachingPage = lazy(() => import('./pages/OCPDExecutiveCoachingPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ShaderDemo = lazy(() => import('./components/ShaderDemo'));

// Loading fallback component - minimal to prevent flash
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-transparent">
    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-main opacity-50"></div>
  </div>
);


const App: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Initialize language from localStorage or URL
    const savedLang = localStorage.getItem('preferred-language');
    const urlLang = window.location.pathname.startsWith('/en') ? 'en' : 'de';
    const lang = savedLang || urlLang;

    if (lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [i18n]);

  const currentLang = i18n.language;

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col overflow-x-hidden w-full">
          <Navigation />

          <main className="flex-1 w-full overflow-x-hidden">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Redirect root to language-specific home */}
                <Route path="/" element={<Navigate to={`/${currentLang}`} replace />} />

                {/* German Routes */}
                <Route path="/de" element={<HomePage />} />
                <Route path="/de/about" element={<AboutPage />} />

                <Route path="/de/services" element={<ServicesPage />} />
                <Route path="/de/services/legacy-wealth" element={<LegacyWealthPage />} />
                <Route path="/de/services/business-consulting" element={<BusinessConsultingPage />} />
                <Route path="/de/services/ocpd-executive-coaching" element={<OCPDExecutiveCoachingPage />} />
                <Route path="/de/blog" element={<BlogPage />} />
                <Route path="/de/blog/:slug" element={<BlogPostPage />} />
                <Route path="/de/faq" element={<FAQPage />} />
                <Route path="/de/contact" element={<ContactPage />} />

                {/* English Routes */}
                <Route path="/en" element={<HomePage />} />
                <Route path="/en/about" element={<AboutPage />} />

                <Route path="/en/services" element={<ServicesPage />} />
                <Route path="/en/services/legacy-wealth" element={<LegacyWealthPage />} />
                <Route path="/en/services/business-consulting" element={<BusinessConsultingPage />} />
                <Route path="/en/services/ocpd-executive-coaching" element={<OCPDExecutiveCoachingPage />} />
                <Route path="/en/blog" element={<BlogPage />} />
                <Route path="/en/blog/:slug" element={<BlogPostPage />} />
                <Route path="/en/faq" element={<FAQPage />} />
                <Route path="/en/contact" element={<ContactPage />} />

                {/* Demo Route */}
                <Route path="/shader-test" element={<ShaderDemo />} />

                {/* Catch all - redirect to home */}
                <Route path="*" element={<Navigate to={`/${currentLang}`} replace />} />
              </Routes>
            </Suspense>
          </main>

          <Footer />
          <ScrollToTop />
        </div>
      </Router>
    </HelmetProvider>
  );
};

export default App;
